import type { Issue } from '../types/issue';

// JIRA-Feld heißt „Custom field (Checklist Text)". Enthält Markdown-artigen
// Text mit Überschriften („# Acceptance Criteria", „# Definition of Done", …)
// und Checkbox-Items der Form „* [x] …" (done) bzw. „* []" oder „* [ ]" (offen).
export const CHECKLIST_FIELD = 'Custom field (Checklist Text)';

export function checklistText(row: Issue): string {
  const v = (row as Record<string, unknown>)[CHECKLIST_FIELD];
  if (v == null || v === '') return '';
  const val = Array.isArray(v) ? (v[0] ?? '') : v;
  return String(val).trim();
}

// ─── Parser ───────────────────────────────────────────────────────────────

export type ChecklistItem = {
  text:  string;
  done:  boolean;
  section: string;   // Überschrift unter der das Item steht, „" falls keine
};

export type ChecklistParsed = {
  raw:        string;
  sections:   string[];      // Überschriften in Reihenfolge
  items:      ChecklistItem[];
  itemsDone:  number;
  itemsOpen:  number;
  freeText:   string;        // Zeilen die weder Überschrift noch Item sind
  hasACHeader: boolean;      // ob mindestens eine Section „Acceptance Criteria"/„AC" heißt
};

// „* [x] …" / „* [X] …" / „- [x] …" — Checkbox-Item (done)
const RE_ITEM_DONE = /^\s*[*-]\s*\[[xX]\]\s*(.*)$/;
// „* [] …" / „* [ ] …" / „- [ ] …" — Checkbox-Item (offen)
const RE_ITEM_OPEN = /^\s*[*-]\s*\[\s*\]\s*(.*)$/;
// „# Überschrift" oder „## Überschrift"
const RE_HEADING   = /^\s*#{1,6}\s+(.+?)\s*$/;

const AC_HEADER_HINT = /acceptance\s*criteria|akzeptanzkriterien|abnahmekriterien|\bAC\b/i;

export function parseChecklist(text: string): ChecklistParsed {
  const out: ChecklistParsed = {
    raw: text, sections: [], items: [],
    itemsDone: 0, itemsOpen: 0, freeText: '',
    hasACHeader: false,
  };
  if (!text) return out;
  const lines = text.split(/\r?\n/);
  let currentSection = '';
  const freeParts: string[] = [];
  for (const line of lines) {
    if (!line.trim()) continue;
    let m = line.match(RE_HEADING);
    if (m) {
      currentSection = m[1].trim();
      out.sections.push(currentSection);
      if (AC_HEADER_HINT.test(currentSection)) out.hasACHeader = true;
      continue;
    }
    m = line.match(RE_ITEM_DONE);
    if (m) {
      out.items.push({ text: m[1].trim(), done: true, section: currentSection });
      out.itemsDone++;
      continue;
    }
    m = line.match(RE_ITEM_OPEN);
    if (m) {
      out.items.push({ text: m[1].trim(), done: false, section: currentSection });
      out.itemsOpen++;
      continue;
    }
    freeParts.push(line.trim());
  }
  out.freeText = freeParts.join('\n').trim();
  return out;
}

// ─── Qualitäts-Buckets ────────────────────────────────────────────────────

export type ChecklistBucket =
  | 'empty'          // Feld ganz leer
  | 'header_only'    // nur Überschriften, keine Items
  | 'no_items'       // Text vorhanden, keine Header, keine Items
  | 'all_open'       // Items da, alle offen
  | 'partial'        // Items da, teilweise done
  | 'all_done';      // alle Items done

export function checklistBucket(p: ChecklistParsed): ChecklistBucket {
  if (!p.raw) return 'empty';
  if (p.items.length === 0) {
    return p.sections.length > 0 ? 'header_only' : 'no_items';
  }
  if (p.itemsDone === 0) return 'all_open';
  if (p.itemsOpen === 0) return 'all_done';
  return 'partial';
}

export const CHECKLIST_LABELS: Record<ChecklistBucket, string> = {
  empty:       'leer',
  header_only: 'nur Überschrift ohne Items',
  no_items:    'Freitext ohne Items',
  all_open:    'alle Items offen',
  partial:     'teilweise erledigt',
  all_done:    'alle Items erledigt',
};

export const CHECKLIST_COLORS: Record<ChecklistBucket, string> = {
  empty:       '#9e9e9e',
  header_only: '#c62828',
  no_items:    '#ef6c00',
  all_open:    '#f57c00',
  partial:     '#1976d2',
  all_done:    '#2e7d32',
};

// ─── Aggregations-Statistiken ─────────────────────────────────────────────

export type ChecklistStats = {
  total:         number;
  withText:      number;
  withoutText:   number;
  withACHeader:  number;
  withItems:     number;
  sumItemsDone:  number;
  sumItemsOpen:  number;
  bucketCounts:  Record<ChecklistBucket, number>;
  perType:       { type: string; total: number; withText: number; withAC: number }[];
  perTeam:       { team: string; total: number; withText: number; withAC: number }[];
};

export function computeChecklistStats(rows: Issue[]): ChecklistStats {
  const bucketCounts: Record<ChecklistBucket, number> = {
    empty: 0, header_only: 0, no_items: 0, all_open: 0, partial: 0, all_done: 0,
  };
  const perTypeMap = new Map<string, { total: number; withText: number; withAC: number }>();
  const perTeamMap = new Map<string, { total: number; withText: number; withAC: number }>();
  let total = 0, withText = 0, withACHeader = 0, withItems = 0;
  let sumItemsDone = 0, sumItemsOpen = 0;

  for (const r of rows) {
    total++;
    const t    = String(r['Issue Type'] ?? '?');
    const team = String(r['Team Name']  ?? '(kein Team)');
    let pt = perTypeMap.get(t); if (!pt) { pt = { total: 0, withText: 0, withAC: 0 }; perTypeMap.set(t, pt); } pt.total++;
    let pm = perTeamMap.get(team); if (!pm) { pm = { total: 0, withText: 0, withAC: 0 }; perTeamMap.set(team, pm); } pm.total++;

    const raw = checklistText(r);
    const p   = parseChecklist(raw);
    const b   = checklistBucket(p);
    bucketCounts[b]++;
    if (raw) {
      withText++;
      pt.withText++;
      pm.withText++;
      sumItemsDone += p.itemsDone;
      sumItemsOpen += p.itemsOpen;
      if (p.items.length > 0) withItems++;
      if (p.hasACHeader) {
        withACHeader++;
        pt.withAC++;
        pm.withAC++;
      }
    }
  }
  return {
    total, withText, withoutText: total - withText,
    withACHeader, withItems, sumItemsDone, sumItemsOpen,
    bucketCounts,
    perType: Array.from(perTypeMap.entries())
      .map(([type, v]) => ({ type, total: v.total, withText: v.withText, withAC: v.withAC }))
      .sort((a, b) => b.total - a.total),
    perTeam: Array.from(perTeamMap.entries())
      .map(([team, v]) => ({ team, total: v.total, withText: v.withText, withAC: v.withAC }))
      .sort((a, b) => b.total - a.total),
  };
}
